/**
 * The history-writing save (backend #439, owner decision). Nothing rewrites a
 * user's past transactions without a stated count:
 *
 *   1. save with `apply_to_past: false` — writes the rule, touches no history;
 *   2. dry-run the SAVED rule by id for the exact, lock-aware, fill-only count;
 *   3. show that count and ask;
 *   4. only then PATCH `apply_to_past: true`, which the server runs as one batch;
 *   5. offer Undo on that batch, by the id the SAVE returned.
 *
 * The sequence runs only when the save would make the server backfill
 * (ruling 5, mirroring `save_triggers_backfill`); every other save is one
 * write that carries the draft's own flag, so a rename never asks and never
 * turns a stored `apply_to_past: true` off.
 *
 * The two flows that can ask live in `useBuilderSave` and `useRuleSwitch`,
 * over what they share in `useRuleBase`. This hook composes them and owns the
 * one thing that spans both: which confirmation holds the screen.
 */
import { track, type Offer, type SaveStep } from './saveShared'
import { useBuilderSave } from './useBuilderSave'
import { useRuleBase } from './useRuleBase'
import { useRuleSwitch } from './useRuleSwitch'

export {
  NOT_APPLIED,
  NOT_CONFIRMED_APPLY,
  NOT_CONFIRMED_SAVE,
  SaveError,
  needsConfirm,
  saveFailureCopy,
  withoutPastCopy,
  type Offer,
  type SaveStep,
  type UndoTarget,
} from './saveShared'

export function useSaveRule({ onDone }: { onDone: () => void }) {
  const base = useRuleBase()
  const builder = useBuilderSave(base, { onDone })
  const sw = useRuleSwitch(base)
  const { step, offer } = builder
  const { switchStep, switchOffer } = sw

  /** The confirmation on screen: whichever was raised first keeps the screen
   *  until it is answered, and the other waits behind it. Swapping a dialog
   *  under a user who is reading it would let their answer land on another rule. */
  const builderUp = offer !== null && (step.k === 'confirm' || step.k === 'applying' || (step.k === 'failed' && step.saved))
  const switchUp = switchOffer !== null && (switchStep.k === 'confirm' || switchStep.k === 'applying')
  const dialog: { offer: Offer; step: SaveStep } | null =
    builderUp && (!switchUp || offer.raised < switchOffer.raised)
      ? { offer, step }
      : switchUp
        ? { offer: switchOffer, step: switchStep }
        : null

  /** Step 4 — and "Try again" after it failed. */
  async function confirm() {
    if (dialog === null || dialog.step.k === 'applying') return
    const { offer: o } = dialog
    if (o.via === 'save') await builder.confirmApply(o)
    else await sw.confirmEnable(o)
  }

  /** The rule is already saved with the flag off (or, from the switch, turned
   *  on with it off): nothing more is written to history. */
  function skipPast(opts: { asDecline?: boolean } = {}) {
    if (dialog === null || dialog.step.k === 'applying') return
    const { offer: o } = dialog
    track('rule_backfill_skipped', { transaction_count: o.count })
    if (o.via === 'save' && opts.asDecline === true) {
      // A direct save has no builder to finish: skipping is declining, the same as Escape. `close` says
      // the past was not touched, and the caller settles the session as declined.
      builder.close()
      return
    }
    if (o.via === 'save') builder.skipSave(o)
    else sw.skipEnable(o)
  }

  /** Back to the builder. A saved rule stays saved, with the flag off. */
  function back() {
    if (dialog === null || dialog.step.k === 'applying') return
    if (dialog.offer.via === 'save') builder.back()
    else sw.resetSwitch()
  }

  return {
    step,
    offer,
    /** The switch's own flow — its count shows on the list, not the builder. */
    switchStep,
    /** What the confirmation dialog is showing, whichever flow owns it. */
    dialog,
    switching: sw.switching,
    undo: base.undo,
    save: builder.save,
    confirm,
    skipPast,
    back,
    begin: builder.begin,
    close: builder.close,
    reset: builder.reset,
    pause: sw.pause,
    enable: sw.enable,
    revertBatch: base.revertBatch,
    closeUndo: base.closeUndo,
    offerUndo: base.offerUndo,
  }
}
